import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { clearFuelDashboard, clearFuelData, clearTanks, getFuelAlerts, getFuelConsumptionOverTime, getFuelDashboardTanks, getFuelTanks, getLastBatches, getLastDispenses } from 'features/fuelManagement/fuelManagementSlice'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Flex, MainHeadline, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { IBatch } from 'types/api/fuelManagementApi.interface'
import { formatDateForDisplay, formatNumbersForDisplay } from 'utils/helpers'
import { AddBatchModal } from './AddBatchModal'
import {
    BurnRateStrip,
    ChartNote,
    ChartTick,
    ChartTickRow,
    DispenseLogFootnote,
    FuelAlarmBanner,
    FuelGrid,
    FuelKpiStrip,
    FuelPageSubtitle,
    FuelWidget,
    HeaderLink,
    MachineRow,
    MachineRowMeta,
    MachineRowName,
    MachineRowRate,
    MachineRowText,
    TankBarScale,
    TankRow,
    TankRowCover,
    TankRowMeta,
    TankRowName,
    TankRowPct,
    TankRowText,
    WidgetBody,
    WidgetLoader,
} from './FuelManagement.styled'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { createPortal } from 'react-dom'
import { AreaChart } from 'components/Chart/AreaChart'
import useQueryPagination from 'hooks/useQueryPagination'
import { fuelConsumptionPeriods, fuelAlertRiskLevels } from './fuelData'
import { parseISO, format, isValid } from 'date-fns'
import { FuelAlertsModal } from './FuelAlertsModal'
import { useNavigate } from 'react-router-dom'
import { TankLevelBar } from './Tanks/TankLevelBar'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { AlertListRow } from 'components/ui/AlertListRow/AlertListRow'
import { AlertList } from 'components/ui/AlertListRow/AlertListRow.styled'
import { tokens } from 'styling/tokens'
import { Button } from 'components/ui/Button/Button'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { AlarmContent, AlarmDot, AlarmText } from 'pages/Operations/Safety/Safety.styled'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { MachineFuelLink } from './Machines/MachineFuelLink'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { IFuelOpsDelivery, IFuelOpsDispenseRow } from 'types/fuelOps.interface'
import { IFuelTankRowVM } from 'types/fuelReconciliation.interface'
import {
    criticalLossBannerText,
    findOpenCriticalLoss,
    formatGhs,
    formatGhsCompact,
    FUEL_BURN_RATES,
    FUEL_CONSUMPTION_14D,
    FUEL_CONSUMPTION_SPIKE_NOTE,
    FUEL_DEMO_DELIVERIES,
    FUEL_DEMO_DISPENSES,
    FUEL_DEMO_LOSSES,
    FUEL_DISPENSE_FOOTNOTE,
    FUEL_DEMO_ALERTS,
    FUEL_LAST_DELIVERY,
    lastDeliveryPricePerL,
    FUEL_OPS_KPIS,
} from './fuelOpsDemoData'
import {
    DEMO_FUEL_TANKS,
    FUEL_COVER_DANGER_DAYS,
    FUEL_COVER_WARNING_DAYS,
    fuelTankEconomicsOf,
} from './fuelTanksDemoData'
import {
    bandDeltaPct,
    bandStateOf,
    FUEL_CONSUMPTION_MACHINES,
    machineHeadlineRateLph,
    signedPctLabel,
    worstFuelMachines,
} from './Machines/fuelConsumptionDemoData'

const riskTone = (level: number): 'danger' | 'warning' | 'info' | 'neutral' =>
  level >= 3 ? 'danger' : level === 2 ? 'warning' : level === 1 ? 'info' : 'neutral'

const shortActionLabel = (label: string): string => {
    const words = label.split(' ')
    return words.length <= 2 ? label : `${words[0]} ${words[words.length - 1]}`
}

const BAND_COLOR = {
    over: tokens.status.danger.fg,
    in: tokens.status.success.fg,
    under: tokens.text.muted,
}

const TANK_TONE_RANK = { danger: 0, warning: 1, success: 2, neutral: 3, info: 3, accent: 3 }

const toTankRow = (tank: any): IFuelTankRowVM => {
    const econ = fuelTankEconomicsOf(tank.name)
    const burnRatePerDay = tank.burnRatePerDay !== undefined ? tank.burnRatePerDay : econ.burnRatePerDay
    const pricePerLitre = tank.pricePerLitre !== undefined ? tank.pricePerLitre : econ.pricePerLitre
    const pct = tank.capacity > 0 ? Math.round((tank.currentAmount / tank.capacity) * 100) : 0
    const sensorAmount = tank.currentSensorAmount ?? null
    const daysOfCover = burnRatePerDay ? Math.round((tank.currentAmount / burnRatePerDay) * 10) / 10 : null
    const overfull = pct >= 100
    let tone: IFuelTankRowVM['tone'] = 'success'
    let statusLabel = 'Ok'
    if (overfull) {
        tone = 'warning'
        statusLabel = 'Overfull — verify dip & meter'
    } else if (daysOfCover == null) {
        tone = 'neutral'
        statusLabel = 'No burn data'
    } else if (daysOfCover < FUEL_COVER_DANGER_DAYS) {
        tone = 'danger'
        statusLabel = 'Reorder now'
    } else if (daysOfCover < FUEL_COVER_WARNING_DAYS) {
        tone = 'warning'
        statusLabel = 'Watch'
    }
    return {
        id: tank.id,
        name: tank.name,
        currentAmount: tank.currentAmount,
        capacity: tank.capacity,
        pct,
        stockValue: tank.currentAmount * pricePerLitre,
        burnRatePerDay,
        daysOfCover,
        tone,
        statusLabel,
        reorderLitres: Math.max(tank.capacity - tank.currentAmount, 0),
        overfull,
        sensorAmount,
        sensorPct: sensorAmount != null && tank.capacity > 0 ? Math.round((sensorAmount / tank.capacity) * 100) : null,
        source: tank,
    }
}

const fmtCover = (days: number | null): string => {
    if (days == null) return '—'
    return Number.isInteger(days) ? `${days} days` : `${days.toFixed(1)} days`
}

const coverColor = (vm: IFuelTankRowVM): string =>
    vm.tone === 'danger' || vm.tone === 'warning' ? tokens.status[vm.tone].fg : tokens.text.muted

const dispenseColumns: IHonestTableColumn<IFuelOpsDispenseRow>[] = [
    { id: 'day', header: 'DAY', cell: (item) => item.dayLabel },
    { id: 'time', header: 'TIME', cell: (item) => item.time },
    { id: 'machine', header: 'MACHINE', cell: (item) => <MachineFuelLink name={item.machine} /> },
    { id: 'operator', header: 'OPERATOR', cell: (item) => item.operator },
    { id: 'liters', header: 'LITRES', cell: (item) => <BodyText weight='bold'>{formatNumbersForDisplay(item.liters)} L</BodyText> },
    { id: 'smu', header: 'SMU AT FILL', cell: (item) => (item.smuHours != null ? formatNumbersForDisplay(item.smuHours) + ' h' : '—') },
    { id: 'tank', header: 'SOURCE', cell: (item) => item.tank },
    {
        id: 'flag',
        header: 'FLAG',
        minWidth: '14rem',
        cell: (item) => (item.flag
            ? <StatusPill status={item.flag.tone} dot={item.flag.tone === 'danger'}>{item.flag.label}</StatusPill>
            : <OtherText grey>—</OtherText>),
    },
]

const batchColumns: IHonestTableColumn<IBatch>[] = [
    { id: 'amount', header: 'Amount', cell: (item) => <BodyText weight='bold'>{formatNumbersForDisplay(item.quantity) || '/'}</BodyText> },
    { id: 'tank', header: 'Fuel Tank', cell: (item) => item?.fuelTank?.name || '/' },
    { id: 'fuelType', header: 'Fuel Type', cell: (item) => item?.fuelType.name || '/' },
    { id: 'supplier', header: 'Supplier', cell: (item) => item?.supplier?.name || '/' },
    { id: 'date', header: 'Date of purchase', cell: (item) => formatDateForDisplay(item.date, true) },
]

const demoDeliveryColumns: IHonestTableColumn<IFuelOpsDelivery>[] = [
    { id: 'amount', header: 'Amount', cell: (item) => <BodyText weight='bold'>{formatNumbersForDisplay(item.liters)} L</BodyText> },
    { id: 'cost', header: 'Cost', cell: (item) => formatGhs(item.costGhs) },
    { id: 'tank', header: 'Fuel Tank', cell: (item) => item.tank },
    { id: 'supplier', header: 'Supplier', cell: (item) => item.supplier },
    { id: 'date', header: 'Date of purchase', cell: (item) => item.dateLabel },
]

export const FuelManagement = () => {
    const {
        batches,
        dispenses,
        dashboardTanks,
        fuelConsumption,
        alerts,
        isLoadingAlerts,
        tanks
    } = useSelector((state: RootState) => state.fuelManagement)
    const [batchesModal, setBatchesModal] = useState(false)
    const [notificationsModal, setNotificationsModal] = useState(false)
    const [selectedPeriod, setSelectedPeriod] = useState(1)
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()

    useQueryPagination({
        fetchFunctions: [getFuelConsumptionOverTime, getLastBatches, getLastDispenses, getFuelDashboardTanks, getFuelAlerts],
        cleanUpFunctions: [clearFuelData, clearFuelDashboard],
        params: null,
        runInParallel: true
    })

    useEffect(() => {
        dispatch(getFuelTanks({ page: 1, limit: 5 }))
        return () => { dispatch(clearTanks()) }
    }, [])
    const data = useMemo(() => {
        if (!fuelConsumption?.points?.length) return [];

        return fuelConsumption.points.map(({ groupByKey, liters }) => {
            const date = parseISO(groupByKey);
            const safeValue = Number(liters) || 0;

            let name = '';

            switch (selectedPeriod) {
                case 1:
                    name = isValid(date) ? format(date, 'MMM') : groupByKey;
                    break;

                case 2:
                    if (isValid(date)) {
                        const day = format(date, 'd');
                        name = day;
                    } else {
                        name = groupByKey;
                    }
                    break;

                case 3:
                    name = isValid(date) ? format(date, 'EEE') : groupByKey;
                    break;

                case 4:
                    name = isValid(date) ? format(date, 'H') : groupByKey;
                    break;

                default:
                    name = groupByKey;
            }

            return {
                name,
                value: safeValue,
            };
        });
    }, [fuelConsumption, selectedPeriod]);

    const alertRows = alerts?.length ? alerts : FUEL_DEMO_ALERTS

    const totalStockLiters = useMemo(
        () => (dashboardTanks?.tanks || []).reduce((sum, tank) => sum + (tank.currentAmount || 0), 0),
        [dashboardTanks]
    )

    const stockLiters = totalStockLiters > 0 ? totalStockLiters : FUEL_OPS_KPIS.stockLiters
    const stockValueGhs = Math.round(stockLiters * FUEL_OPS_KPIS.dieselPriceGhs)
    const spendDeltaPct = (((FUEL_OPS_KPIS.spendMonthGhs - FUEL_OPS_KPIS.spendLastMonthGhs) / FUEL_OPS_KPIS.spendLastMonthGhs) * 100).toFixed(1)

    const criticalLoss = findOpenCriticalLoss(FUEL_DEMO_LOSSES)

    const usingDemoConsumption = USE_OPS_MOCKS && (!data.length || data.every((d) => !d.value))
    const chartData = usingDemoConsumption ? FUEL_CONSUMPTION_14D : data
    const tickStep = Math.max(1, Math.ceil(chartData.length / 7))
    const chartTicks = chartData.filter((_, index) => index % tickStep === 0)

    const tankRows: IFuelTankRowVM[] = useMemo(() => {
        const source: any[] = tanks?.data?.length ? tanks.data : USE_OPS_MOCKS ? DEMO_FUEL_TANKS : []
        return source
            .map(toTankRow)
            .sort((a, b) =>
                TANK_TONE_RANK[a.tone] - TANK_TONE_RANK[b.tone] ||
                (a.daysOfCover ?? Infinity) - (b.daysOfCover ?? Infinity))
            .slice(0, 5)
    }, [tanks])

    const worstMachines = worstFuelMachines(FUEL_CONSUMPTION_MACHINES)

    const usingDemoDispenses = !dispenses?.data?.length && FUEL_DEMO_DISPENSES.length > 0
    const dispenseRows: IFuelOpsDispenseRow[] = useMemo(() => {
        if (dispenses?.data?.length) {
            return dispenses.data.map((d) => ({
                id: d.id,
                dayLabel: formatDateForDisplay(d.dispensedAt),
                time: d.dispensedAt && isValid(new Date(d.dispensedAt)) ? format(new Date(d.dispensedAt), 'HH:mm') : '—',
                machine: d.machine?.name || '/',
                operator: d.user ? d.user.firstName + ' ' + d.user.lastName : '/',
                liters: d.quantity,
                smuHours: d.engineHours ?? null,
                tank: d.fromFuelTank?.name || '/',
            }))
        }
        return FUEL_DEMO_DISPENSES
    }, [dispenses])

    const handleConsumption = (period: number) => {
        setSelectedPeriod(period)
        dispatch(getFuelConsumptionOverTime(period))
    }

    return <>
        <NavBar />
        <PageWrapper noFooter>
            <Flex between center>
                <Flex center gap='0.75rem'>
                    <MainHeadline>Fuel Management</MainHeadline>
                    <DemoBadge />
                </Flex>
                <Button variant='primary' onClick={() => setBatchesModal(true)}>
                    Add Batch
                </Button>
            </Flex>
            <FuelPageSubtitle>Stock, burn and dispense activity across site tanks</FuelPageSubtitle>
            {criticalLoss && (
                <FuelAlarmBanner>
                    <AlarmContent>
                        <AlarmDot />
                        <AlarmText>{criticalLossBannerText(criticalLoss)}</AlarmText>
                    </AlarmContent>
                    <Button variant='danger' size='sm' onClick={() => navigate('/fuel/losses')}>
                        View loss
                    </Button>
                </FuelAlarmBanner>
            )}
            {USE_OPS_MOCKS && (
                <FuelKpiStrip>
                    <MetricCard
                        value={`${formatNumbersForDisplay(stockLiters)} L`}
                        label='STOCK ON SITE'
                        sublabel={`${formatGhs(stockValueGhs)} · GH₵ ${FUEL_OPS_KPIS.dieselPriceGhs.toFixed(2)}/L wtd avg · last ${lastDeliveryPricePerL.toFixed(2)}`}
                        accentColor={tokens.accent.base}
                    />
                    <MetricCard
                        value={`${formatNumbersForDisplay(FUEL_OPS_KPIS.burnLitersPerDay)} L/day`}
                        label='BURN'
                        sublabel={`≈ ${formatGhsCompact(FUEL_OPS_KPIS.burnMonthGhs)} / month`}
                        accentColor={tokens.accent.base}
                    />
                    <MetricCard
                        value={formatGhsCompact(FUEL_OPS_KPIS.spendMonthGhs)}
                        label='SPEND THIS MONTH'
                        sublabel={`${formatGhsCompact(FUEL_OPS_KPIS.spendLastMonthGhs)} last month`}
                        accentColor={tokens.status.warning.fg}
                        trailing={<StatusPill status='warning' dot>▲ +{spendDeltaPct}%</StatusPill>}
                    />
                    <MetricCard
                        value={`${FUEL_OPS_KPIS.intensityLitersPerTonne} L / t moved`}
                        label='FUEL INTENSITY'
                        sublabel='feeds ops cost-per-tonne'
                        accentColor={tokens.accent.base}
                    />
                    {FUEL_LAST_DELIVERY && (
                        <MetricCard
                            value={`${FUEL_LAST_DELIVERY.dateLabel} · ${formatNumbersForDisplay(FUEL_LAST_DELIVERY.liters)} L`}
                            label='LAST DELIVERY'
                            sublabel={`${formatGhs(FUEL_LAST_DELIVERY.costGhs)} · ${FUEL_LAST_DELIVERY.supplier}`}
                            accentColor={tokens.status.success.fg}
                        />
                    )}
                </FuelKpiStrip>
            )}
            <FuelGrid>
                <FuelWidget $span={4}>
                    <ContainerHeader>
                        <Flex width='100%' between center>
                            <TitleText>Tanks</TitleText>
                            <HeaderLink type='button' onClick={() => navigate('/fuel/tanks')}>View all ›</HeaderLink>
                        </Flex>
                    </ContainerHeader>
                    <WidgetBody>
                        {tanks?.isLoading ? (
                            <Flex width='100%' height='100%' justcenter center><WidgetLoader /></Flex>
                        ) : !tankRows.length ? (
                            <Flex width='100%'><NoItems title='Fuel Tanks' /></Flex>
                        ) : (
                            tankRows.map((vm) => (
                                <TankRow key={vm.id}>
                                    <TankBarScale>
                                        <TankLevelBar
                                            pct={vm.pct}
                                            tone={vm.tone}
                                            currentAmount={vm.currentAmount}
                                            capacity={vm.capacity}
                                            sensorPct={vm.sensorPct}
                                            sensorAmount={vm.sensorAmount}
                                        />
                                    </TankBarScale>
                                    <TankRowText>
                                        <TankRowName>{vm.name}</TankRowName>
                                        <TankRowMeta>{`${formatNumbersForDisplay(vm.currentAmount)} / ${formatNumbersForDisplay(vm.capacity)} L`}</TankRowMeta>
                                    </TankRowText>
                                    <TankRowPct>{`${vm.pct}%`}</TankRowPct>
                                    <TankRowCover $color={coverColor(vm)}>{fmtCover(vm.daysOfCover)}</TankRowCover>
                                </TankRow>
                            ))
                        )}
                    </WidgetBody>
                </FuelWidget>

                <FuelWidget $span={4}>
                    <ContainerHeader>
                        <Flex width='100%' between center>
                            <TitleText>Consumption</TitleText>
                            <HeaderLink type='button' onClick={() => navigate('/fuel/machines')}>View all ›</HeaderLink>
                        </Flex>
                    </ContainerHeader>
                    <ContainerContent gap='0.75rem'>
                        {usingDemoConsumption ? (
                            <OtherText color={tokens.text.muted}>Last 14 days · L/day</OtherText>
                        ) : (
                            <TabsContainer>
                                {fuelConsumptionPeriods.map(p => (
                                    <Tabs key={p.value} active={selectedPeriod === p.value} onClick={() => handleConsumption(p.value)}>
                                        {p.label}
                                    </Tabs>
                                ))}
                            </TabsContainer>
                        )}
                        {!chartData.length || chartData.every(d => !d.value) ? (
                            <Flex height='150px' width='100%' center justcenter><OtherText>No data available</OtherText></Flex>
                        ) : (
                            <Flex column width='100%' gap='0.5rem'>
                                <AreaChart data={chartData} height={180} />
                                <ChartTickRow>
                                    {chartTicks.map((tick, index) => <ChartTick key={`${tick.name}_${index}`}>{tick.name}</ChartTick>)}
                                </ChartTickRow>
                            </Flex>
                        )}
                        {usingDemoConsumption && (
                            <ChartNote>
                                <OtherText grey>{FUEL_CONSUMPTION_SPIKE_NOTE}</OtherText>
                            </ChartNote>
                        )}
                    </ContainerContent>
                </FuelWidget>

                <FuelWidget $span={4}>
                    <ContainerHeader>
                        <Flex width='100%' between center>
                            <TitleText>Alerts</TitleText>
                            <HeaderLink type='button' onClick={() => setNotificationsModal(true)}>View all ›</HeaderLink>
                        </Flex>
                    </ContainerHeader>
                    <WidgetBody>
                        {isLoadingAlerts ? (
                            <Flex height='100%' width='100%' justcenter center><WidgetLoader /></Flex>
                        ) : alertRows.length ? (
                            <AlertList>
                                {alertRows.slice(0, 6).map((a) => {
                                    const riskWord = fuelAlertRiskLevels[a.riskLevel]
                                    const hasAction = a.actions?.length > 0
                                    return (
                                        <AlertListRow
                                            key={a.id}
                                            title={a.summary}
                                            meta={a.machine?.name ? `${a.machine.name} · ${riskWord}` : riskWord}
                                            pillLabel={hasAction ? shortActionLabel(a.actions[0]) : riskWord}
                                            pillTone={riskTone(a.riskLevel)}
                                            onPillClick={hasAction ? () => setNotificationsModal(true) : undefined}
                                        />
                                    )
                                })}
                            </AlertList>
                        ) : (
                            <OtherText grey>No alerts this week</OtherText>
                        )}
                    </WidgetBody>
                </FuelWidget>

                {worstMachines.length > 0 && (
                    <FuelWidget $span={4}>
                        <ContainerHeader>
                            <Flex width='100%' between center>
                                <TitleText>Machines to watch</TitleText>
                                <HeaderLink type='button' onClick={() => navigate('/fuel/machines')}>View all ›</HeaderLink>
                            </Flex>
                        </ContainerHeader>
                        <WidgetBody>
                            {worstMachines.map((m) => {
                                const headline = machineHeadlineRateLph(m)
                                const delta = bandDeltaPct(headline, m.nominalBurnRateLph)
                                const state = bandStateOf(headline, m.nominalBurnRateLph)
                                const topFlag = m.flags[0]
                                return (
                                    <MachineRow key={m.id} onClick={() => navigate(`/fuel/machines/${m.id}`)}>
                                        <MachineRowText>
                                            <MachineRowName>{m.name}</MachineRowName>
                                            <MachineRowMeta>{`nominal ${m.nominalBurnRateLph} L/h · ${m.model}`}</MachineRowMeta>
                                        </MachineRowText>
                                        <MachineRowRate $color={BAND_COLOR[state]}>
                                            {`${headline} L/h · ${signedPctLabel(delta)}`}
                                        </MachineRowRate>
                                        {topFlag ? (
                                            <StatusPill status={topFlag.tone} dot={topFlag.tone === 'danger'}>{topFlag.label}</StatusPill>
                                        ) : (
                                            <StatusPill status='success'>IN BAND</StatusPill>
                                        )}
                                    </MachineRow>
                                )
                            })}
                        </WidgetBody>
                    </FuelWidget>
                )}

                <FuelWidget $span={worstMachines.length ? 8 : 6}>
                    <ContainerHeader>
                        <Flex width='100%' between center>
                            <TitleText>Recent batches</TitleText>
                            <HeaderLink type='button' onClick={() => navigate('/fuel/dashboard/batches')}>View all ›</HeaderLink>
                        </Flex>
                    </ContainerHeader>
                    <ContainerContent>
                        {batches?.data.length ? (
                            <HonestTable columns={batchColumns} data={batches.data.slice(0, 5)} keyField='id' />
                        ) : FUEL_DEMO_DELIVERIES.length ? (
                            <HonestTable columns={demoDeliveryColumns} data={FUEL_DEMO_DELIVERIES.slice(0, 5)} keyField='id' />
                        ) : (
                            <Flex width='100%' height='100%' center justcenter><NoItems title='Batches' /></Flex>
                        )}
                    </ContainerContent>
                </FuelWidget>

                <FuelWidget $span={worstMachines.length ? 12 : 6}>
                    <ContainerHeader>
                        <Flex width='100%' between center>
                            <TitleText>Dispenses — yesterday & today</TitleText>
                            <HeaderLink type='button' onClick={() => navigate('/fuel/dashboard/dispenses')}>View all ›</HeaderLink>
                        </Flex>
                    </ContainerHeader>
                    <ContainerContent gap='1rem'>
                        {usingDemoDispenses && FUEL_BURN_RATES.length > 0 && (
                            <BurnRateStrip>
                                <BodyText grey>L / engine-hour by machine:</BodyText>
                                {FUEL_BURN_RATES.map((r) => (
                                    <Flex key={r.machine} center gap='0.5rem'>
                                        <BodyText weight='bold' color={r.investigate ? tokens.status.danger.fg : tokens.text.primary}>
                                            {r.machine} {r.litersPerEngineHour}
                                        </BodyText>
                                        {r.investigate && <StatusPill status='danger' dot>investigate</StatusPill>}
                                    </Flex>
                                ))}
                            </BurnRateStrip>
                        )}
                        {dispenseRows.length ? (
                            <HonestTable columns={dispenseColumns} data={dispenseRows.slice(0, 6)} keyField='id' />
                        ) : (
                            <NoItems title='Dispenses' />
                        )}
                        <DispenseLogFootnote>
                            <OtherText color={tokens.text.muted}>{FUEL_DISPENSE_FOOTNOTE}</OtherText>
                        </DispenseLogFootnote>
                    </ContainerContent>
                </FuelWidget>
            </FuelGrid>
            {batchesModal && <AddBatchModal closeModal={() => setBatchesModal(false)} />}
            {notificationsModal && createPortal(<FuelAlertsModal closeModal={() => setNotificationsModal(false)} />, document.body)}

        </PageWrapper >

    </>
}
